import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

export type ThemeMode = 'system' | 'light' | 'dark';

// Shared with the public marketing site (D:\_LATEST\FN-FrontEnd) so the theme
// mechanism is identical across FN properties.
const STORAGE_KEY = 'fn_theme';

// =============================================================================
// ThemeService — light / dark / system, applied as a body-level `.dark-mode`
// class. Same model and storage key as the public website's ThemeService, so
// the behaviour is consistent: the toggle cycles system -> light -> dark ->
// system, the choice is persisted, and 'system' follows the OS preference live.
//
// Difference from the marketing site: that site defaults to 'system'. This app
// defaults to 'light' so existing users aren't flipped into the (newer) dark
// skin automatically — they opt in via the toggle. Flip the initial value to
// 'system' once the dark skin has been fully QA'd across every screen.
// =============================================================================
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly _mode = new BehaviorSubject<ThemeMode>('light');
  /** The user's chosen mode (system/light/dark), for the toggle icon. */
  readonly mode$ = this._mode.asObservable();

  private readonly _isDark = new BehaviorSubject<boolean>(false);
  /** The resolved dark state after applying the system preference. */
  readonly isDark$ = this._isDark.asObservable();

  private systemPrefersDark = false;
  private readonly isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        this._mode.next(stored);
      }

      const mq = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)');
      if (mq) {
        this.systemPrefersDark = mq.matches;
        const onChange = (e: MediaQueryListEvent) => {
          this.systemPrefersDark = e.matches;
          this.resolveDark();
        };
        if (mq.addEventListener) {
          mq.addEventListener('change', onChange);
        } else if ((mq as any).addListener) {
          (mq as any).addListener(onChange);
        }
      }
    }

    this.resolveDark();
  }

  get mode(): ThemeMode { return this._mode.value; }
  get isDark(): boolean { return this._isDark.value; }

  /** Material icon name for the toggle button. */
  get icon(): string {
    return this.mode === 'system' ? 'contrast'
      : this.mode === 'light' ? 'light_mode'
      : 'dark_mode';
  }

  /** Accessible label / tooltip for the toggle button. */
  get label(): string {
    return this.mode === 'system' ? 'Theme: system (click for light)'
      : this.mode === 'light' ? 'Theme: light (click for dark)'
      : 'Theme: dark (click for system)';
  }

  /** Cycle system -> light -> dark -> system. */
  cycle(): void {
    const next: ThemeMode =
      this.mode === 'system' ? 'light' :
      this.mode === 'light' ? 'dark' :
      'system';
    this.setMode(next);
  }

  setMode(mode: ThemeMode): void {
    this._mode.next(mode);
    if (this.isBrowser) {
      try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* private mode / quota */ }
    }
    this.resolveDark();
  }

  private resolveDark(): void {
    const dark = this.mode === 'dark' || (this.mode === 'system' && this.systemPrefersDark);
    this._isDark.next(dark);
    if (this.isBrowser) {
      document.body.classList.toggle('dark-mode', dark);
      this.applySyncfusionTheme(dark);
    }
  }

  // Swap Syncfusion's built-in dark theme in/out. The dark theme is a separate,
  // non-injected bundle (`syncfusion-dark.css`, configured in angular.json).
  // Appending its <link> at the end of <head> makes its `.e-*` rules override
  // the light Material theme from the main bundle; removing it restores light.
  private static readonly SF_DARK_LINK_ID = 'syncfusion-dark-theme';
  private applySyncfusionTheme(dark: boolean): void {
    if (!this.isBrowser) { return; }
    const id = ThemeService.SF_DARK_LINK_ID;
    const existing = document.getElementById(id) as HTMLLinkElement | null;
    if (dark) {
      if (!existing) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = 'syncfusion-dark.css';
        document.head.appendChild(link);
      }
    } else if (existing && existing.parentNode) {
      existing.parentNode.removeChild(existing);
    }
  }
}
